<template>
  <popup @close-popup="closePopup">
    <template v-slot:content>
      <div class="payment-review">
        <span class="title payment-review__title"> Review your payment </span>

        <div class="payment-review__table">
          <div class="payment-review__row">
            <div class="payment-review__flexStart">
              <div class="payment-review__header">Repayment plan</div>
              <p class="payment-review__subheader">Pay in {{ numberOfInstallments }} instalments</p>
            </div>
            <div class="payment-review__flexEnd">
              <p class="payment-review__link" @click="changePaymentPlan">Change</p>
            </div>
          </div>
          <div class="payment-review__row">
            <div class="payment-review__flexStart">
              <div class="payment-review__header">Due today</div>
              <p class="payment-review__subheader">{{ dayOfToday }}</p>
            </div>
            <div class="payment-review__flexEnd">
              <p class="payment-review__bold">
                {{
                  moneyFormatter(breakdown[0].amountDue / 100, {
                    countryCode: 'ng',
                    showEmptyDecimal: false,
                    displayCurrencyAs: 'symbol',
                    officialLanguage: 'en'
                  })
                }}
              </p>
            </div>
          </div>
          <div class="payment-review__row">
            <div class="payment-review__flexStart">
              <div class="payment-review__header">Total cost</div>
            </div>
            <div class="payment-review__flexEnd">
              <p class="payment-review__bold">
                {{
                  moneyFormatter(store.state.zeroWidgetProps.totalPrice / 100, {
                    countryCode: 'ng',
                    showEmptyDecimal: false,
                    displayCurrencyAs: 'symbol',
                    officialLanguage: 'en'
                  })
                }}
              </p>
            </div>
          </div>
          <div v-if="store.state.checkCardAvailability === 'available'" class="payment-review__payment-instrument">
            <img
              class="payment-review-cardLogo"
              :src="store.state.paymentInstrumentType === 'CARD' ? cardLogo : WalletLogo"
              v-if="!!cardLogo"
              alt="card-logo"
            />
            <Skeletor width="70" height="30" v-else pill />
            <span class="payment-review-cardNumber" v-if="store.state.paymentInstrumentType === 'CARD'"
              ><span>**** </span> {{ store.state.cardDetails.lastFourDigits || 1111 }}</span
            >
            <p class="payment-review__link" @click.prevent="changePaymentMethod">Change payment method</p>
          </div>
          <div class="payment-review__error" v-if="store.state.checkCardAvailability === 'unavailable'">
            We could not retrieve card details of this user
          </div>
        </div>

        <div class="payment-review__message">
          <custom-input
            @input-event="inputChange"
            input-name="payment-method"
            input-type="checkbox"
            input-value="privacy-policy-terms-&-services"
            input-id="t&s"
          >
            <template v-slot:label-content>
              <span>
                By clicking continue, you agree to the <a href="/">Carbon Zero Terms of Service</a> and Carbon’s
                <a href="/">Privacy Policy.</a></span
              >
            </template>
          </custom-input>
        </div>

        <div class="btn btn__primary btn--lg">
          <button @click="makeDownPayment" :disabled="store.state.checkCardAvailability === 'unavailable' || !termsAndConditionChecked">
            Pay
            <span>
              {{
                moneyFormatter(breakdown[0].amountDue / 100, {
                  countryCode: 'ng',
                  showEmptyDecimal: false,
                  displayCurrencyAs: 'symbol',
                  officialLanguage: 'en'
                })
              }}</span
            >
            now
          </button>
        </div>
      </div>
    </template>
  </popup>
</template>

<script lang="ts" setup>
import { Skeletor } from 'vue-skeletor'
import 'vue-skeletor/dist/vue-skeletor.css'
import { onBeforeMount, ref, watch, computed } from 'vue'
import Popup from '@/components/cards/popup/index.vue'
import store from '@/composables/useZeroWidgetStore/methods'
import CustomInput from '@/components/custom-radio-or-check-input/index.vue'
import { moneyFormatter, userCardIcon, getDateOrdinal } from '@/composables/useUtilities'
import WalletLogo from '@/assets/icons/WalletLogo.svg'

const termsAndConditionChecked = ref(false)

const inputChange = (target: HTMLInputElement) => {
  termsAndConditionChecked.value = target.checked
}

const breakdown = ref([
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' },
  { amountDue: 0, amountPaid: 0, dueDate: '', late: false, paidDate: '', principalPaid: '', state: '' }
])
const cardLogo: any = ref('')
onBeforeMount(async () => {
  breakdown.value = [...store.state.repaymentSchedule]
  store.actions.getCardDetails()
})
const numberOfInstallments = computed(() => store.state.repaymentSchedule.length - 1)
const cardDetails = computed(() => {
  return store.state.cardDetails
})

const changePaymentPlan = () => {
  store.actions.setComponent('payment-plan')
}

const changePaymentMethod = () => {
  store.actions.setComponent('change-payment-method')
}

const dayOfToday = computed(() => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  const date = new Date()
  const today = date.getDate()
  const thisMonth = date.getMonth()
  const ordinal = getDateOrdinal(today)
  return `${today}${ordinal} of ${months[thisMonth]}`
})
const makeDownPayment = () => {
  if (store.state.checkCardAvailability) {
    const payload = {
      duration: store.state.repaymentSchedule.length - 1,
      unit: 'MONTH',
      merchantApiKey: store.state.zeroWidgetProps.apiKey,
      purchaseRefId: store.state.zeroWidgetProps.purchaseRefId,
      paymentInstrumentType: store.state.paymentInstrumentType
    }
    store.actions.makeDownPayment(payload)
  }
}

const emit = defineEmits<{
  (e: 'close-popup'): void
}>()
const closePopup = () => {
  return emit('close-popup')
}

watch(cardDetails, async () => {
  cardLogo.value = JSON.parse(JSON.stringify(await userCardIcon(cardDetails.value.brand))).default
})
</script>
<style lang=""></style>
